import { c as _c } from "react/compiler-runtime";
/**
 * A React hook that adds markdown formatting shortcuts to a textarea
 * Supports common markdown syntax like bold, italic, links, and lists
 * Uses keyboard shortcuts (Cmd/Ctrl + key) to trigger formatting
 */

import type { RefObject } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { calculateNewCursorPosition, formatters, getLineSelection, getTextSelection, updateTextAreaValue } from '../utils/markdown-formatters';
type UseMarkdownShortcutsProps = {
  textAreaRef: RefObject<HTMLTextAreaElement | null>;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  setTriggerAutoSize: (value: string) => void;
  onValueChange?: (value: string, selectionStart: number) => void;
};
export const useMarkdownShortcuts = t0 => {
  const $ = _c(34);
  const {
    textAreaRef,
    onChange,
    setTriggerAutoSize,
    onValueChange
  } = t0;
  let t1;
  if ($[0] !== onChange || $[1] !== onValueChange || $[2] !== setTriggerAutoSize || $[3] !== textAreaRef) {
    t1 = formatFn => {
      const textarea = textAreaRef.current;
      if (!textarea) {
        return;
      }
      if (textarea !== document.activeElement) {
        return;
      }
      const {
        beforeText,
        selectedText,
        afterText,
        start
      } = getTextSelection(textarea);
      const formattedText = formatFn(selectedText);
      const newValue = beforeText + formattedText + afterText;
      const newCursorPosition = calculateNewCursorPosition(start, selectedText, formattedText);
      onValueChange?.(newValue, newCursorPosition);
      updateTextAreaValue(textarea, newValue, newCursorPosition, setTriggerAutoSize, onChange);
    };
    $[0] = onChange;
    $[1] = onValueChange;
    $[2] = setTriggerAutoSize;
    $[3] = textAreaRef;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const formatSelectedText = t1;
  let t2;
  if ($[5] !== onChange || $[6] !== onValueChange || $[7] !== setTriggerAutoSize || $[8] !== textAreaRef) {
    t2 = formatFn_0 => {
      const textarea_0 = textAreaRef.current;
      if (!textarea_0) {
        return;
      }
      if (textarea_0 !== document.activeElement) {
        return;
      }
      if (textarea_0.selectionStart !== textarea_0.selectionEnd) {
        const {
          beforeText: beforeText_0,
          selectedText: selectedText_0,
          afterText: afterText_0,
          start: start_0
        } = getTextSelection(textarea_0);
        const lines = selectedText_0.split("\n");
        const formattedLines = lines.map((line, i) => formatFn_0(line, i));
        const formattedText_0 = formattedLines.join("\n");
        const newValue_0 = beforeText_0 + formattedText_0 + afterText_0;
        const newCursorPosition_0 = start_0 + formattedText_0.length;
        onValueChange?.(newValue_0, newCursorPosition_0);
        updateTextAreaValue(textarea_0, newValue_0, newCursorPosition_0, setTriggerAutoSize, onChange);
        return;
      }
      const {
        beforeLineStart,
        currentLine,
        afterLineEnd,
        lineStart
      } = getLineSelection(textarea_0);
      if (textarea_0.selectionStart === lineStart && currentLine.trim() === "") {
        const formattedLine = `${formatFn_0("").trim()} `;
        const newValue_1 = beforeLineStart + formattedLine + afterLineEnd;
        const newCursorPosition_1 = lineStart + formattedLine.length;
        onValueChange?.(newValue_1, newCursorPosition_1);
        updateTextAreaValue(textarea_0, newValue_1, newCursorPosition_1, setTriggerAutoSize, onChange);
        return;
      }
      if (currentLine === "") {
        return;
      }
      const formattedLine_0 = formatFn_0(currentLine);
      const newValue_2 = beforeLineStart + formattedLine_0 + afterLineEnd;
      const newCursorPosition_2 = lineStart + formattedLine_0.length;
      onValueChange?.(newValue_2, newCursorPosition_2);
      updateTextAreaValue(textarea_0, newValue_2, newCursorPosition_2, setTriggerAutoSize, onChange);
    };
    $[5] = onChange;
    $[6] = onValueChange;
    $[7] = setTriggerAutoSize;
    $[8] = textAreaRef;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const formatLineStart = t2;
  let t3;
  if ($[10] !== formatSelectedText) {
    t3 = e => {
      e.preventDefault();
      formatSelectedText(formatters.bold);
    };
    $[10] = formatSelectedText;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      enableOnFormTags: true
    };
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useHotkeys("mod+b", t3, t4);
  let t5;
  if ($[13] !== formatSelectedText) {
    t5 = e_0 => {
      e_0.preventDefault();
      formatSelectedText(formatters.italic);
    };
    $[13] = formatSelectedText;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      enableOnFormTags: true
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useHotkeys("mod+i", t5, t6);
  let t7;
  if ($[16] !== formatSelectedText) {
    t7 = e_1 => {
      e_1.preventDefault();
      formatSelectedText(formatters.code);
    };
    $[16] = formatSelectedText;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      enableOnFormTags: true
    };
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  useHotkeys("mod+e", t7, t8);
  let t9;
  if ($[19] !== formatSelectedText) {
    t9 = e_2 => {
      e_2.preventDefault();
      formatSelectedText(formatters.link);
    };
    $[19] = formatSelectedText;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      enableOnFormTags: true
    };
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  useHotkeys("mod+k", t9, t10);
  let t11;
  if ($[22] !== formatSelectedText) {
    t11 = e_3 => {
      e_3.preventDefault();
      formatSelectedText(formatters.strikethrough);
    };
    $[22] = formatSelectedText;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      enableOnFormTags: true
    };
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  useHotkeys("mod+shift+x", t11, t12);
  let t13;
  if ($[25] !== formatLineStart) {
    t13 = e_4 => {
      e_4.preventDefault();
      formatLineStart(formatters.orderedList);
    };
    $[25] = formatLineStart;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      enableOnFormTags: true
    };
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  useHotkeys("mod+shift+7", t13, t14);
  let t15;
  if ($[28] !== formatLineStart) {
    t15 = e_5 => {
      e_5.preventDefault();
      formatLineStart(formatters.unorderedList);
    };
    $[28] = formatLineStart;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      enableOnFormTags: true
    };
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  useHotkeys("mod+shift+8", t15, t16);
  let t17;
  if ($[31] !== formatLineStart) {
    t17 = e_6 => {
      e_6.preventDefault();
      formatLineStart(formatters.blockquote);
    };
    $[31] = formatLineStart;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      enableOnFormTags: true
    };
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  useHotkeys("mod+shift+9", t17, t18);
};